import { useContext, useEffect, useMemo, useState } from "react";
import { signOut } from "firebase/auth";
import { collection, getDocs, query, where, documentId } from "firebase/firestore";
import { auth, db } from "../firebase/firebase";
import { AuthContext } from "../context/AuthContext";
import "./DocsHub.css";

const DAYS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

function sortSessions(a, b) {
  if (a.dayOfWeek !== b.dayOfWeek) return a.dayOfWeek - b.dayOfWeek;
  return (a.startTime || "").localeCompare(b.startTime || "");
}
function parseYmd(ymd) {
  if (!ymd) return null;
  const [y, m, d] = ymd.split("-").map(Number);
  const dt = new Date(y, m - 1, d);
  dt.setHours(0, 0, 0, 0);
  return dt;
}

function fmtYmd(ymd) {
  const dt = parseYmd(ymd);
  if (!dt) return "—";
  return dt.toLocaleDateString(undefined, { day: "2-digit", month: "short", year: "numeric" });
}

function unitStatus(unit, today) {
  const s = parseYmd(unit.startDate);
  const e = parseYmd(unit.endDate);
  if (!s || !e) return "unknown";
  if (today < s) return "upcoming";
  if (today > e) return "done";
  return "current";
}


export default function StudentDashboard() {
  const { userData } = useContext(AuthContext);

  const [loading, setLoading] = useState(false);

  const [sessions, setSessions] = useState([]);
  const [courseMap, setCourseMap] = useState({}); // {courseId: {id,name,...}}
  const [termName, setTermName] = useState("");

  const [unitsByCourse, setUnitsByCourse] = useState({});
  const [activeCourseId, setActiveCourseId] = useState("");

  const today = useMemo(() => {
    const d = new Date();
    d.setHours(0, 0, 0, 0);
    return d;
  }, []);



  const courseIds = userData?.courseIds || [];
  const termId = userData?.currentTermId || null;

  // Load term name + course names (so UI shows names not ids)
  useEffect(() => {
    (async () => {
      if (!userData) return;
      if (userData.role !== "student") return;

      setTermName("");
      setCourseMap({});

      try {
        // Term name
        if (termId) {
          const tSnap = await getDocs(query(collection(db, "terms"), where(documentId(), "==", termId)));
          const t = tSnap.docs[0]?.data();
          setTermName(t?.name || "");
        }

        // Course map
        if (courseIds.length) {
          // Firestore "in" max 10
          const chunks = [];
          for (let i = 0; i < courseIds.length; i += 10) chunks.push(courseIds.slice(i, i + 10));

          const snaps = await Promise.all(
            chunks.map((chunk) =>
              getDocs(query(collection(db, "courses"), where(documentId(), "in", chunk)))
            )
          );

          const map = {};
          snaps.flatMap((s) => s.docs).forEach((d) => {
            map[d.id] = { id: d.id, ...d.data() };
          });
          setCourseMap(map);
        }
      } catch (e) {
        console.error(e);
      }
    })();
  }, [userData, termId, courseIds]);

  useEffect(() => {
    if (!activeCourseId && courseIds.length) setActiveCourseId(courseIds[0]);
  }, [courseIds, activeCourseId]);


  // Load timetable sessions
  useEffect(() => {
    (async () => {
      if (!userData) return;
      if (userData.role !== "student") return;

      setLoading(true);
      setSessions([]);

      try {
        if (!termId || !courseIds.length) {
          setSessions([]);
          return;
        }

        const chunks = [];
        for (let i = 0; i < courseIds.length; i += 10) chunks.push(courseIds.slice(i, i + 10));

        const results = await Promise.all(
          chunks.map((chunk) =>
            getDocs(
              query(
                collection(db, "timetables"),
                where("termId", "==", termId),
                where("courseId", "in", chunk)
              )
            )
          )
        );

        const all = results
          .flatMap((snap) => snap.docs.map((d) => ({ id: d.id, ...d.data() })))
          .sort(sortSessions);

        setSessions(all);
      } catch (e) {
        console.error(e);
        setSessions([]);
      } finally {
        setLoading(false);
      }
    })();
  }, [userData, termId, courseIds]);

  // Load units per course for this term
  useEffect(() => {
    (async () => {
      if (!userData) return;
      if (userData.role !== "student") return;

      setUnitsByCourse({});

      try {
        if (!termId || !courseIds.length) return;

        const chunks = [];
        for (let i = 0; i < courseIds.length; i += 10) chunks.push(courseIds.slice(i, i + 10));

        const snaps = await Promise.all(
          chunks.map((chunk) =>
            getDocs(
              query(
                collection(db, "termUnits"),
                where("termId", "==", termId),
                where("courseId", "in", chunk)
              )
            )
          )
        );

        // termUnits docs are per (termId, courseId)
        const map = {};
        snaps.flatMap((s) => s.docs).forEach((d) => {
          const data = d.data();
          map[data.courseId] = Array.isArray(data.units) ? data.units : [];
        });

        setUnitsByCourse(map);
      } catch (e) {
        console.error(e);
      }
    })();
  }, [userData, termId, courseIds]);

  const groupedByDay = useMemo(() => {
    const m = new Map();
    for (const s of sessions) {
      const key = s.dayOfWeek ?? 0;
      if (!m.has(key)) m.set(key, []);
      m.get(key).push(s);
    }
    return Array.from(m.entries()).sort((a, b) => a[0] - b[0]);
  }, [sessions]);

  const handleLogout = async () => {
    await signOut(auth);
  };

  const courseLabel = (courseId) => courseMap[courseId]?.name || courseId;

  return (
    <div className="content-area">
      <div className="docs-shell">
        <div className="docs-header-card">
          <div className="page-header" style={{ marginBottom: 0 }}>
            <div className="page-title">🗓️ My Timetable</div>
            <div className="page-subtitle">Your classes + units for the current term.</div>
          </div>

          <div className="docs-header-actions docs-header-actions-right">
            <div className="docs-user-pill">
              <span>{userData?.email}</span>
              <span className="docs-role-badge">{userData?.role || "user"}</span>
            </div>
            <button className="btn btn-secondary" onClick={handleLogout}>
              Logout
            </button>
          </div>
        </div>

        <div style={{ height: 16 }} />

        <div className="docs-panel">
          <div className="docs-panel-title">
            Term: {termName || "—"}
          </div>

          {!termId ? (
            <div className="docs-info">No term assigned. Please contact admin.</div>
          ) : !courseIds.length ? (
            <div className="docs-info">No courses assigned to your account yet.</div>
          ) : (
            <div className="student-side">

              {/* Course Tabs */}
              <div className="student-tabs">
                {courseIds.map((cid) => (
                  <button
                    key={cid}
                    type="button"
                    className={`student-tab ${activeCourseId === cid ? "is-active" : ""}`}
                    onClick={() => setActiveCourseId(cid)}
                  >
                    {courseLabel(cid)}
                  </button>
                ))}
              </div>

              {/* Units List */}
              <div className="student-units-card" style={{ marginTop: 12 }}>
                {(() => {
                  const cid = activeCourseId || courseIds[0];
                  const units = (unitsByCourse[cid] || [])
                    .slice()
                    .sort((a, b) => (a.startDate || "").localeCompare(b.startDate || ""));

                  if (!units.length) {
                    return <div className="docs-muted">No units posted yet.</div>;
                  }

                  return (
                    <div className="student-units-list">
                      {units.map((u) => {
                        const status = unitStatus(u, today);
                        return (
                          <div
                            key={u.id || `${u.title}-${u.startDate}`}
                            className={`student-unit-row2 ${status}`}
                          >
                            <div className="unit-main">
                              <div className="unit-title">{u.title}</div>
                              <div className="unit-dates">
                                {fmtYmd(u.startDate)} → {fmtYmd(u.endDate)}
                              </div>
                            </div>

                            <div className={`unit-badge ${status}`}>
                              {status === "current"
                                ? "Current"
                                : status === "upcoming"
                                ? "Upcoming"
                                : "Done"}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  );
                })()}
              </div>
            </div>
          )}
        </div>

      </div>
    </div>
  );
}
